import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  useDisclosure,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { FormPhoneInput } from '../../../core';
import { Api } from '../../../api/api';

// Global reCAPTCHA v3 declaration
declare global {
  interface Window {
    grecaptcha: any;
  }
}

interface QRFormData {
  phoneNumber: string;
}

interface OTPFormData {
  otpCode: string;
}

const QRPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [otpError, setOtpError] = useState<string>('');
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const codeFromQuery = searchParams.get('code');

  // If no code parameter, redirect or show error
  if (!codeFromQuery) {
    return (
      <Box maxW="md" mx="auto" mt={8} p={6} borderRadius="lg" boxShadow="lg">
        <Alert status="error">
          <AlertIcon />
          Geçersiz QR kod parametresi. URL&apos;de &apos;code&apos; parametresi bulunmalıdır.
        </Alert>
      </Box>
    );
  }

  const validationSchema = yup.object({
    phoneNumber: yup
      .string()
      .matches(/^[0-9]{10}$/, 'Telefon numarası 10 haneli olmalıdır')
      .required('Telefon numarası gereklidir'),
  });

  const formik = useFormik<QRFormData>({
    initialValues: {
      phoneNumber: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      setError('');
      setSuccess('');

      try {
        const recaptchaToken = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {
          action: 'qr_validate',
        });

        const response = await Api.Qr.validate({
          phoneNumber: values.phoneNumber,
          code: codeFromQuery,
          recaptchaToken,
        });

        if (response.isSuccess && response.data) {
          onOpen();
        } else {
          setError(response.message || 'Doğrulama başarısız. Lütfen telefon numaranızı kontrol edin.');
        }
      } catch (err: any) {
        setError(err.message || 'Bir hata oluştu');
      } finally {
        setIsLoading(false);
      }
    },
  });

  const otpFormik = useFormik<OTPFormData>({
    initialValues: {
      otpCode: '',
    },
    validationSchema: yup.object({
      otpCode: yup
        .string()
        .matches(/^[0-9]{6}$/, 'Doğrulama kodu 6 haneli olmalıdır')
        .required('Doğrulama kodu gereklidir'),
    }),
    onSubmit: async (values) => {
      setIsOTPLoading(true);
      setOtpError('');

      const response = await Api.Qr.verifyCode({
        phoneNumber: formik.values.phoneNumber,
        code: parseInt(codeFromQuery!, 10),
        validationCode: values.otpCode,
      });

      if (response.isSuccess === true && response.data instanceof Blob) {
        // PDF başarılı durumu - sadece başarılı ve Blob ise
        const pdfObjectUrl = URL.createObjectURL(response.data);
        setPdfUrl(pdfObjectUrl);
        setSuccess('Doğrulama başarılı! Raporunuz görüntüleniyor...');
        onClose();
        otpFormik.resetForm();
      } else {
        // Hata durumu - Global error handler'dan gelen mesaj
        setOtpError(response.message || 'Doğrulama başarısız.');
      }

      setIsOTPLoading(false);
    },
  });

  // Load reCAPTCHA v3 script
  useEffect(() => {
    const loadRecaptcha = () => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.grecaptcha.ready(() => {
          setRecaptchaLoaded(true);
        });
      };
      document.head.appendChild(script);
    };

    if (!window.grecaptcha) {
      loadRecaptcha();
    } else {
      setRecaptchaLoaded(true);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // PDF gösteriliyorsa fullscreen görünüm
  if (pdfUrl) {
    return (
      <Box position="fixed" top={0} left={0} right={0} bottom={0} bg="gray.100" zIndex={1000}>
        {/* PDF Viewer - Full Height */}
        <Box height="calc(100vh)" width="100%">
          <iframe src={pdfUrl} width="100%" height="100%" style={{ border: 'none' }} title="Ekspertiz Raporu" />
        </Box>
      </Box>
    );
  }

  return (
    <Box maxW="md" mx="auto" mt={8} p={6} borderRadius="lg" boxShadow="lg">
      <VStack spacing={6}>
        <Text fontSize="2xl" fontWeight="bold" textAlign="center">
          Rapor Doğrulama
        </Text>

        <form onSubmit={formik.handleSubmit}>
          <VStack spacing={4} w="100%">
            <FormControl isInvalid={!!(formik.errors.phoneNumber && formik.touched.phoneNumber)}>
              <FormLabel>Telefon Numarası</FormLabel>
              <FormPhoneInput
                name="phoneNumber"
                placeholder="5xxxxxxxxx"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <FormErrorMessage>{formik.errors.phoneNumber}</FormErrorMessage>
            </FormControl>

            <Button
              type="submit"
              colorScheme="green"
              size="lg"
              w="100%"
              isLoading={isLoading}
              loadingText="Gönderiliyor..."
              isDisabled={!formik.values.phoneNumber || !recaptchaLoaded}
            >
              Doğrulama Kodu Gönder
            </Button>
          </VStack>
        </form>

        {/* Error and Success Messages */}
        {error && (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {success && (
          <Alert status="success">
            <AlertIcon />
            {success}
          </Alert>
        )}

        {/* OTP Modal */}
        <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Doğrulama Kodu</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={4}>Telefonunuza gönderilen 6 haneli doğrulama kodunu girin:</Text>
              <form onSubmit={otpFormik.handleSubmit}>
                <FormControl isInvalid={!!(otpFormik.errors.otpCode && otpFormik.touched.otpCode)}>
                  <Input
                    name="otpCode"
                    placeholder="123456"
                    value={otpFormik.values.otpCode}
                    onChange={otpFormik.handleChange}
                    onBlur={otpFormik.handleBlur}
                    maxLength={6}
                    textAlign="center"
                    fontSize="lg"
                  />
                  <FormErrorMessage>{otpFormik.errors.otpCode}</FormErrorMessage>
                </FormControl>
                {otpError && (
                  <Alert status="error" mt={3}>
                    <AlertIcon />
                    {otpError}
                  </Alert>
                )}
              </form>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                İptal
              </Button>
              <Button
                colorScheme="blue"
                onClick={() => otpFormik.handleSubmit()}
                isLoading={isOTPLoading}
                loadingText="Doğrulanıyor..."
                isDisabled={!otpFormik.values.otpCode}
              >
                Doğrula
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export { QRPage };
